// src/components/ui/index.jsx
// Centralized reusable UI components — Pastel Design System

import React from 'react';

// Design tokens (matching pastel system)
const T = {
  brand: '#B5A2D2',
  brandDeep: '#6B5876',
  accent: '#C9929A',
  accentSoft: '#E8B5BC',
  peach: '#DDB48E',
  surface: '#ffffff',
  s2: '#FAF7FB',
  s3: '#F0EDF3',
  border: '#E8E5EC',
  text: '#3D3447',
  text2: '#6B6070',
  muted: '#A8A1B0',
  success: '#8FC2A6',
  warning: '#DDB48E',
  danger: '#C9929A',
  info: '#A5B8D6',
  successBg: '#E0EFE5', successFg: '#2F5C42',
  warningBg: '#F5E4D2', warningFg: '#80551F',
  dangerBg: '#F0DADD', dangerFg: '#7A3C44',
  infoBg: '#DDE5F0', infoFg: '#3D4F70',
  accentBg: '#E6DEF0', accentFg: '#5A4E66',
  serif: "'Instrument Serif', Georgia, serif",
  sans: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif",
};

export function Badge({ variant = 'neutral', children }) {
  const styles = {
    success: { background: T.successBg, color: T.successFg },
    danger: { background: T.dangerBg, color: T.dangerFg },
    warning: { background: T.warningBg, color: T.warningFg },
    info: { background: T.infoBg, color: T.infoFg },
    neutral: { background: T.s3, color: T.text2 },
    accent: { background: T.accentBg, color: T.accentFg },
  };
  return (
    <span style={{ display:'inline-flex',alignItems:'center',gap:4,padding:'4px 10px',borderRadius:999,fontSize:11,fontWeight:600,letterSpacing:.3,fontFamily:T.sans,...styles[variant] }}>
      {children}
    </span>
  );
}

export function StatusBadge({ status }) {
  const map = {
    confirmed:         <Badge variant="success">✓ Confirmado</Badge>,
    pending:           <Badge variant="warning">⏳ Pendente</Badge>,
    waitingConfirm:    <Badge variant="accent">📲 Aguard. confirmação</Badge>,
    cancelled:         <Badge variant="danger">✗ Cancelado</Badge>,
    rescheduleRequested: <Badge variant="info">🔄 Troca solicitada</Badge>,
  };
  return map[status] || <Badge>—</Badge>;
}

export function Avatar({ name = '', color = 'rose', size = 'md' }) {
  const initials = name.split(' ').map(w=>w[0]).slice(0,2).join('').toUpperCase();
  const sizes = { sm: 28, md: 36, lg: 48, xl: 60 };
  const fontSize = { sm: 11, md: 13, lg: 18, xl: 22 };
  const grads = {
    rose: 'linear-gradient(135deg,#C9929A,#DDB48E)',
    lavender: 'linear-gradient(135deg,#B5A2D2,#C9B6E4)',
    mint: 'linear-gradient(135deg,#8FC2A6,#B5D4C0)',
    peach: 'linear-gradient(135deg,#DDB48E,#E8C9AC)',
    mixed: 'linear-gradient(135deg,#B5A2D2,#C9929A)',
    // Legacy aliases
    red: 'linear-gradient(135deg,#C9929A,#DDB48E)',
    blue: 'linear-gradient(135deg,#B5A2D2,#C9B6E4)',
    green: 'linear-gradient(135deg,#8FC2A6,#B5D4C0)',
    gold: 'linear-gradient(135deg,#DDB48E,#E8C9AC)',
    purple: 'linear-gradient(135deg,#B5A2D2,#C9929A)',
  };
  const s = sizes[size];
  return (
    <div style={{ width:s,height:s,borderRadius:'50%',background:grads[color]||grads.rose,display:'flex',alignItems:'center',justifyContent:'center',fontSize:fontSize[size],fontWeight:600,color:'#fff',flexShrink:0 }}>
      {initials}
    </div>
  );
}

export function Button({ variant = 'primary', size = 'md', children, onClick, disabled, loading, style = {}, type = 'button' }) {
  const isDisabled = disabled || loading;
  const base = { display:'inline-flex',alignItems:'center',gap:6,borderRadius:12,fontWeight:500,cursor:isDisabled?'not-allowed':'pointer',border:'none',transition:'all 0.15s',fontFamily:T.sans,opacity:isDisabled?0.65:1 };
  const sizes = { sm: { padding:'6px 12px',fontSize:12,borderRadius:8 }, md: { padding:'8px 16px',fontSize:13 }, lg: { padding:'12px 24px',fontSize:15 } };
  const variants = {
    primary: { background:T.brand,color:'#fff' },
    secondary: { background:T.s3,color:T.text,border:`1px solid ${T.border}` },
    success: { background:T.successBg,color:T.successFg,border:`1px solid ${T.success}` },
    danger: { background:T.dangerBg,color:T.dangerFg,border:`1px solid ${T.danger}` },
    warning: { background:T.warningBg,color:T.warningFg,border:`1px solid ${T.warning}` },
    ghost: { background:'transparent',color:T.text2 },
  };
  return (
    <button type={type} onClick={onClick} disabled={isDisabled}
      style={{ ...base, ...sizes[size], ...variants[variant], ...style }}>
      {loading && (
        <span style={{ width:12,height:12,border:'2px solid currentColor',borderTopColor:'transparent',
                       borderRadius:'50%',display:'inline-block',animation:'spin 0.6s linear infinite' }} />
      )}
      {children}
    </button>
  );
}

export function Card({ children, style = {} }) {
  return (
    <div style={{ background:T.surface,border:`1px solid ${T.border}`,borderRadius:20,overflow:'hidden',...style }}>
      {children}
    </div>
  );
}

export function CardHeader({ title, subtitle, action }) {
  return (
    <div style={{ padding:'20px 24px',borderBottom:`1px solid ${T.border}`,display:'flex',alignItems:'center',justifyContent:'space-between' }}>
      <div>
        <div style={{ fontFamily:T.serif,fontSize:18,color:T.text }}>{title}</div>
        {subtitle && <div style={{ fontSize:13,color:T.muted,marginTop:2 }}>{subtitle}</div>}
      </div>
      {action}
    </div>
  );
}

export function Input({ label, error, ...props }) {
  return (
    <div style={{ marginBottom: 18 }}>
      {label && <label style={{ display:'block',fontSize:13,fontWeight:500,color:T.text2,marginBottom:6 }}>{label}</label>}
      <input style={{ width:'100%',padding:'10px 14px',border:`1.5px solid ${error?T.danger:T.border}`,borderRadius:12,fontSize:14,fontFamily:T.sans,color:T.text,background:T.surface,outline:'none',boxSizing:'border-box' }}
        {...props} />
      {error && <div style={{ fontSize:12,color:T.dangerFg,marginTop:4 }}>{error}</div>}
    </div>
  );
}

export function Select({ label, children, error, ...props }) {
  return (
    <div style={{ marginBottom: 18 }}>
      {label && <label style={{ display:'block',fontSize:13,fontWeight:500,color:T.text2,marginBottom:6 }}>{label}</label>}
      <select style={{ width:'100%',padding:'10px 14px',border:`1.5px solid ${error?T.danger:T.border}`,borderRadius:12,fontSize:14,fontFamily:T.sans,color:T.text,background:T.surface,outline:'none',appearance:'none',backgroundImage:"url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23666' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E\")",backgroundRepeat:'no-repeat',backgroundPosition:'right 12px center',paddingRight:36,boxSizing:'border-box' }}
        {...props}>
        {children}
      </select>
    </div>
  );
}

export function Modal({ open, onClose, title, children, footer, size = 'md' }) {
  if (!open) return null;
  const maxWidths = { sm: 420, md: 520, lg: 680, xl: 800 };
  return (
    <div onClick={(e)=>{ if(e.target===e.currentTarget) onClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 300,
        background: 'rgba(61,52,71,0.45)', backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        // padding inclui safe-area para não colar em notches/home indicator
        padding: 'max(16px, env(safe-area-inset-top)) 16px max(16px, env(safe-area-inset-bottom)) 16px',
        boxSizing: 'border-box',
      }}>
      <div style={{
        background: T.surface, borderRadius: 20,
        boxShadow: '0 12px 40px rgba(74,63,88,0.12)',
        width: '100%', maxWidth: maxWidths[size],
        maxHeight: '90vh',
        display: 'flex', flexDirection: 'column', // flex column para footer fixo
        animation: 'slideUp 0.25s ease',
        overflow: 'hidden', // clip border-radius
      }}>
        {/* Header fixo no topo do modal */}
        <div style={{
          padding: '20px 24px 16px', flexShrink: 0,
          borderBottom: `1px solid ${T.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div style={{ fontFamily: T.serif, fontSize: 20, color: T.text,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            paddingRight: 12 }}>
            {title}
          </div>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            fontSize: 22, color: T.muted, flexShrink: 0, lineHeight: 1,
            padding: '4px 8px', borderRadius: 8,
          }}>×</button>
        </div>
        {/* Conteúdo scrollável */}
        <div style={{ padding: '20px 24px', overflowY: 'auto', flex: 1 }}>{children}</div>
        {/* Footer fixo na base do modal */}
        {footer && (
          <div style={{
            padding: '12px 24px 20px', flexShrink: 0,
            display: 'flex', justifyContent: 'flex-end', gap: 10, flexWrap: 'wrap',
            borderTop: `1px solid ${T.border}`,
          }}>
            {footer}
          </div>
        )}
      </div>
      <style>{`@keyframes slideUp { from { opacity:0; transform:translateY(12px); } to { opacity:1; transform:translateY(0); } }`}</style>
    </div>
  );
}

export function Toast({ message, type = 'success' }) {
  const colors = { success:T.success, danger:T.danger, warning:T.warning, info:T.info };
  const icons  = { success:'✓', danger:'✗', warning:'⚠', info:'ℹ' };
  return (
    <div style={{
      background: T.brandDeep, color: '#fff',
      padding: '14px 20px', borderRadius: 12,
      boxShadow: '0 8px 30px rgba(74,63,88,0.2)',
      display: 'flex', alignItems: 'center', gap: 10,
      fontSize: 14, fontFamily: T.sans,
      borderLeft: `4px solid ${colors[type] || T.success}`,
      wordBreak: 'break-word',
    }}>
      <span style={{ color: colors[type] || T.success, fontWeight: 700, fontSize: 16, flexShrink: 0 }}>
        {icons[type] || '•'}
      </span>
      {message}
    </div>
  );
}

export function EmptyState({ icon = '📭', title, text, action }) {
  return (
    <div style={{ textAlign:'center',padding:'60px 20px',color:T.muted }}>
      <div style={{ fontSize:48,marginBottom:12,opacity:0.5 }}>{icon}</div>
      <div style={{ fontSize:16,fontWeight:600,color:T.text2,marginBottom:6 }}>{title}</div>
      {text && <div style={{ fontSize:14,lineHeight:1.6 }}>{text}</div>}
      {action && <div style={{ marginTop:20 }}>{action}</div>}
    </div>
  );
}

export function StatCard({ label, value, delta, deltaUp = true, icon, accent = 'lavender' }) {
  const accents = {
    rose: T.accent, lavender: T.brand, mint: T.success, peach: T.peach,
    red: T.accent,  green: T.success,  gold: T.peach,  blue: T.info,
  };
  return (
    <div style={{
      background: T.surface, border: `1px solid ${T.border}`, borderRadius: 20,
      padding: '20px 24px', position: 'relative', overflow: 'hidden',
      minWidth: 0, // necessário para truncar dentro de flex/grid
    }}>
      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
        <span style={{ width:6, height:6, borderRadius:'50%', background:accents[accent]||T.brand, opacity:.55, flexShrink:0 }} />
        <div style={{
          fontSize:12, fontWeight:500, color:T.muted, textTransform:'uppercase',
          letterSpacing:1, fontFamily:T.sans,
          overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
        }}>
          {label}
        </div>
      </div>
      <div style={{
        fontFamily: T.serif, fontSize: 32, color: T.text, margin: '2px 0 4px', lineHeight: 1,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        paddingRight: icon ? 48 : 0, // espaço para o ícone decorativo
      }}>
        {value}
      </div>
      {delta && (
        <div style={{ fontSize:12, fontFamily:T.sans, color:T.muted, display:'flex', alignItems:'center', gap:4 }}>
          <span style={{ opacity:.7 }}>{deltaUp ? '↑' : '↓'}</span>
          <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{delta}</span>
        </div>
      )}
      {icon && (
        <div style={{ position:'absolute', right:20, top:'50%', transform:'translateY(-50%)', fontSize:40, opacity:0.08, pointerEvents:'none' }}>
          {icon}
        </div>
      )}
    </div>
  );
}

export function Spinner() {
  return (
    <div style={{ display:'flex',alignItems:'center',justifyContent:'center',padding:60 }}>
      <div style={{ width:36,height:36,border:`3px solid ${T.border}`,borderTopColor:T.brand,borderRadius:'50%',animation:'spin 0.8s linear infinite' }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

export function Alert({ type = 'info', title, children, actions }) {
  const styles = {
    confirm: { border:`1px solid ${T.warning}`,background:T.warningBg,color:T.warningFg,icon:'⏰' },
    info: { border:`1px solid ${T.info}`,background:T.infoBg,color:T.infoFg,icon:'ℹ️' },
    success: { border:`1px solid ${T.success}`,background:T.successBg,color:T.successFg,icon:'✅' },
    danger: { border:`1px solid ${T.danger}`,background:T.dangerBg,color:T.dangerFg,icon:'❌' },
    warning: { border:`1px solid ${T.warning}`,background:T.warningBg,color:T.warningFg,icon:'⚠️' },
    reschedule: { border:`1px solid ${T.info}`,background:T.infoBg,color:T.infoFg,icon:'🔄' },
  };
  const s = styles[type] || styles.info;
  return (
    <div style={{ padding:'16px 20px',borderRadius:12,...s,display:'flex',gap:12,marginBottom:16,fontFamily:T.sans }}>
      <div style={{ fontSize:18,flexShrink:0,marginTop:1 }}>{s.icon}</div>
      <div style={{ flex:1 }}>
        {title && <div style={{ fontWeight:600,marginBottom:4 }}>{title}</div>}
        <div style={{ fontSize:13,opacity:0.9 }}>{children}</div>
        {actions && <div style={{ display:'flex',gap:8,marginTop:10,flexWrap:'wrap' }}>{actions}</div>}
      </div>
    </div>
  );
}

export function FormGroup({ label, required, children, hint }) {
  return (
    <div style={{ marginBottom: 18 }}>
      {label && (
        <label style={{ display:'block',fontSize:13,fontWeight:500,color:T.text2,marginBottom:6 }}>
          {label} {required && <span style={{ color:T.danger }}>*</span>}
        </label>
      )}
      {children}
      {hint && <div style={{ fontSize:11,color:T.muted,marginTop:4 }}>{hint}</div>}
    </div>
  );
}

export function FilterChip({ label, active, onClick }) {
  return (
    <div onClick={onClick} style={{
      padding:'6px 14px', borderRadius:999, cursor:'pointer', fontSize:12, fontWeight:500,
      border:`1px solid ${active ? T.brand : T.border}`,
      background: active ? T.brand : T.s3,
      color: active ? '#fff' : T.text2,
      transition:'all 0.15s', userSelect:'none',
    }}>
      {label}
    </div>
  );
}

export function SearchBox({ value, onChange, placeholder = 'Buscar...' }) {
  return (
    <div style={{ position:'relative' }}>
      <span style={{ position:'absolute',left:12,top:'50%',transform:'translateY(-50%)',color:T.muted,fontSize:14,pointerEvents:'none' }}>🔍</span>
      <input
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        style={{ width:'100%',padding:'9px 14px 9px 36px',border:`1.5px solid ${T.border}`,borderRadius:12,fontSize:14,fontFamily:T.sans,color:T.text,outline:'none',boxSizing:'border-box' }}
      />
    </div>
  );
}

export function Loading({ text = 'Carregando...' }) {
  return (
    <div style={{ display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',minHeight:'40vh',gap:12,color:T.muted }}>
      <div style={{ width:36,height:36,border:`3px solid ${T.border}`,borderTopColor:T.brand,borderRadius:'50%',animation:'spin 0.8s linear infinite' }} />
      <span style={{ fontSize:14 }}>{text}</span>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

export function ProcedureCard({ procedure, onClick, actions }) {
  const formatCur = (v) => Number(v||0).toLocaleString('pt-BR',{style:'currency',currency:'BRL'});
  return (
    <div onClick={onClick} style={{ background:T.surface,border:`1px solid ${T.border}`,borderRadius:20,overflow:'hidden',transition:'all 0.2s',cursor:onClick?'pointer':'default' }}>
      <div style={{ height:110,background:`linear-gradient(135deg,${T.brand},${T.accent})`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:40 }}>
        {procedure.emoji || '💆'}
      </div>
      <div style={{ padding:16 }}>
        <div style={{ fontWeight:600,fontSize:15,color:T.text,marginBottom:4 }}>{procedure.name}</div>
        <div style={{ fontSize:12,color:T.muted,marginBottom:10 }}>{procedure.category} • {procedure.duration}min</div>
        <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom: actions?12:0 }}>
          <div style={{ fontSize:16,fontWeight:700,color:T.accent }}>{formatCur(procedure.price)}</div>
          {procedure.approved
            ? <span style={{ background:T.successBg,color:T.successFg,fontSize:11,fontWeight:600,padding:'3px 8px',borderRadius:999 }}>✓ Aprovado</span>
            : <span style={{ background:T.warningBg,color:T.warningFg,fontSize:11,fontWeight:600,padding:'3px 8px',borderRadius:999 }}>⏳ Pendente</span>}
        </div>
        {procedure.professionalName && (
          <div style={{ fontSize:12,color:T.muted,marginBottom: actions?8:0 }}>👤 {procedure.professionalName}</div>
        )}
        {actions}
      </div>
    </div>
  );
}

export function Table({ headers, children, emptyState }) {
  return (
    <div style={{ overflowX:'auto' }}>
      <table style={{ width:'100%',borderCollapse:'collapse',fontSize:13,fontFamily:T.sans }}>
        <thead>
          <tr>
            {headers.map((h,i) => (
              <th key={i} style={{ textAlign:'left',padding:'10px 16px',fontSize:11,fontWeight:600,textTransform:'uppercase',letterSpacing:1,color:T.muted,borderBottom:`1px solid ${T.border}`,background:T.s2,whiteSpace:'nowrap' }}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
      {(!children || (Array.isArray(children) && children.length === 0)) && emptyState}
    </div>
  );
}

// ── TextArea ──────────────────────────────────────────────────
export function TextArea({ label, value, onChange, placeholder, maxLength, hint, error }) {
  return (
    <div style={{ marginBottom: 18 }}>
      {label && <label style={{ display:'block',fontSize:13,fontWeight:500,color:T.text2,marginBottom:6 }}>{label}</label>}
      <textarea 
        value={value} 
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        maxLength={maxLength}
        style={{ 
          width:'100%', 
          minHeight:100, 
          padding:'12px 14px', 
          border:`1.5px solid ${error?T.danger:T.border}`, 
          borderRadius:12, 
          fontSize:14, 
          fontFamily:T.sans, 
          color:T.text, 
          background:T.surface, 
          outline:'none', 
          boxSizing:'border-box',
          resize:'vertical'
        }} 
      />
      {hint && <div style={{ fontSize:11, color:T.muted, marginTop:4 }}>{hint}</div>}
      {maxLength && value && (
        <div style={{ fontSize:11, color:T.muted, marginTop:4, textAlign:'right' }}>
          {value.length}/{maxLength}
        </div>
      )}
      {error && <div style={{ fontSize:12,color:T.dangerFg,marginTop:4 }}>{error}</div>}
    </div>
  );
}

// ── ChipMultiSelect ──────────────────────────────────────────
export function ChipMultiSelect({ options, value = [], onChange, renderOption }) {
  const toggleOption = (optId) => {
    const newValue = value.includes(optId)
      ? value.filter(id => id !== optId)
      : [...value, optId];
    onChange(newValue);
  };

  return (
    <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
      {options.map(opt => {
        const selected = value.includes(opt.id);
        return (
          <div key={opt.id} onClick={() => toggleOption(opt.id)}
            style={{
              padding:'6px 12px',
              borderRadius:999,
              border:`1.5px solid ${selected ? T.brand : T.border}`,
              background: selected ? T.accentBg : T.surface,
              color: selected ? T.accentFg : T.text2,
              fontSize:13,
              fontWeight:500,
              cursor:'pointer',
              transition:'all .15s',
              display:'inline-flex',
              alignItems:'center',
              gap:6,
              fontFamily:T.sans,
            }}>
            {selected && <span style={{ fontSize:10 }}>✓</span>}
            {renderOption ? renderOption(opt, selected) : opt.label}
          </div>
        );
      })}
    </div>
  );
}

// ── PhotoUpload ──────────────────────────────────────────────
// Comprime a imagem antes de converter para base64 para não exceder o limite de
// 1 MB por documento do Firestore. Redimensiona para no máximo 400px e usa JPEG 75%.
function compressImage(file, maxPx = 400, quality = 0.75) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > height && width > maxPx) {
          height = Math.round((height * maxPx) / width);
          width  = maxPx;
        } else if (height > maxPx) {
          width  = Math.round((width * maxPx) / height);
          height = maxPx;
        }
        const canvas = document.createElement('canvas');
        canvas.width  = width;
        canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', quality));
      };
      img.src = ev.target?.result;
    };
    reader.readAsDataURL(file);
  });
}

export function PhotoUpload({ value, onChange, emoji = '📷', shape = 'circle' }) {
  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const compressed = await compressImage(file);
      onChange(compressed);
    } catch {
      // fallback sem compressão se o browser não suportar Canvas
      const reader = new FileReader();
      reader.onload = (ev) => onChange(ev.target?.result);
      reader.readAsDataURL(file);
    }
  };

  const radius = shape === 'circle' ? '50%' : '16px';

  return (
    <div style={{ display:'flex', alignItems:'center', gap:16 }}>
      <div style={{ 
        width:96, 
        height:96, 
        borderRadius:radius,
        background: value ? `url(${value}) center/cover` : `linear-gradient(135deg,${T.brand},${T.accent})`,
        display:'flex',
        alignItems:'center',
        justifyContent:'center',
        fontSize:40,
        color:'#fff',
        flexShrink:0,
        border:`2px solid ${T.border}`,
      }}>
        {!value && emoji}
      </div>
      <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
        <label style={{ cursor:'pointer' }}>
          <input type="file" accept="image/*" onChange={handleFileChange} style={{ display:'none' }} />
          <div style={{ 
            padding:'8px 16px', 
            background:T.s3, 
            color:T.text, 
            borderRadius:10, 
            fontSize:13, 
            fontWeight:500,
            border:`1px solid ${T.border}`,
            cursor:'pointer',
            fontFamily:T.sans,
            display:'inline-block',
          }}>
            {value ? 'Trocar foto' : 'Escolher foto'}
          </div>
        </label>
        {value && (
          <button onClick={() => onChange(null)} 
            style={{ 
              padding:'6px 12px', 
              background:'transparent', 
              color:T.dangerFg, 
              border:'none',
              borderRadius:8, 
              fontSize:12, 
              cursor:'pointer',
              fontFamily:T.sans,
              textAlign:'left',
            }}>
            Remover
          </button>
        )}
      </div>
    </div>
  );
}
